import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import "./TravelInsurance.css";
import API from "../../utils/API";
import { CiCalendar } from "react-icons/ci";
import { FaTrash, FaPlus, FaMinus } from "react-icons/fa6";
import { MdArrowDropUp } from "react-icons/md";
import { IoMdArrowDropdown } from "react-icons/io";
import { FaAngleRight } from "react-icons/fa";
import { FaAngleLeft } from "react-icons/fa";
import {
  setEnquiryData,
  setEnquiryMeta,
} from "../../redux/slices/EnquirySlice";
import { toast } from "react-toastify";
import {
  resetMastersData,
  updateMastersData,
} from "../../redux/slices/MastersDataSlice";
import {
  resetTravellerDetails,
  updateTravellerDetails,
} from "../../redux/slices/TravellerDetailsSlice";
import { resetQuoteDetails } from "../../redux/slices/QuoteDetailsSlice";
import { RxCross1 } from "react-icons/rx";
import { iso2Map } from "../utility/utilityFunctions";

// ── ISO2 code map keyed by CountryName (matches API CountryName exactly) ─────

const policyBaseUrl =
  process.env.NODE_ENV === "development"
    ? "https://preprod.smcinsurance.com"
    : "https://smcinsurance.com";

// Convert ISO2 code to a flag emoji using regional indicator symbols
// This works natively in modern browsers and renders as a proper flag image
function isoToFlagEmoji(isoCode) {
  if (!isoCode || isoCode.length !== 2) return "🌐";
  const codePoints = [...isoCode.toUpperCase()].map(
    (char) => 0x1f1e6 - 65 + char.charCodeAt(0),
  );
  return String.fromCodePoint(...codePoints);
}

// FlagImg: tries <img> from multiple CDNs, falls back to emoji
function FlagImg({ countryName, size = 24 }) {
  const code = iso2Map[countryName];
  const [imgError, setImgError] = useState(false);

  if (!code) {
    return <span style={{ fontSize: size, lineHeight: 1 }}>🌐</span>;
  }

  if (imgError) {
    return (
      <span
        style={{
          fontSize: size,
          lineHeight: 1,
          fontFamily:
            "Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif",
        }}
      >
        {isoToFlagEmoji(code)}
      </span>
    );
  }

  return (
    <img
      src={`https://hatscripts.github.io/circle-flags/flags/${code.toLowerCase()}.svg`}
      width={size * 1} // rectangle ratio
      height={size}
      alt={countryName}
      style={{
        objectFit: "cover",
        verticalAlign: "middle",
        flexShrink: 0,
        display: "inline-block",
      }}
      onError={() => setImgError(true)}
    />
  );
}

const pad2 = (n) => String(n).padStart(2, "0");
const toYMD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const addDaysYMD = (ymd, days) => {
  const base = parseYMD(ymd);
  if (!base) return "";
  const next = new Date(base);
  next.setDate(next.getDate() + (Number(days) || 0));
  return toYMD(next);
};
const formatPretty = (s) => {
  const d = parseYMD(s);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(d);
};

const HOME_PAGE_SELF_RELATION_ID = 69;
const HOME_PAGE_MAX_MEMBERS = 4;

const getTripPlanKeyFromEnquiry = (enquiryData) => {
  const raw = String(
    enquiryData?.tripPlan ||
      enquiryData?.TripPlan ||
      enquiryData?.tripType ||
      "",
  )
    .trim()
    .toLowerCase();

  if (raw.includes("student")) return "student";
  if (raw.includes("annual")) return "annualMultiTrip";
  return "single";
};

const getPolicyType = (tripType, travellerCount) => {
  const normalizedTripType = String(tripType || "").toLowerCase();
  if (normalizedTripType.includes("student")) return "Student";
  return travellerCount === 1 ? "Individual" : "Family";
};

const getMobileValidationError = (value) => {
  const v = String(value || "");
  if (!v) return "";
  if (!/^[6-9]\d{9}$/.test(v)) {
    return "Mobile must start with 6, 7, 8, or 9 and be 10 digits";
  }
  if (/^(\d)\1{9}$/.test(v)) {
    return "Invalid mobile number";
  }
  return "";
};

const getHomePageEnquiryState = (enquiryData) => {
  const destinationList = Array.isArray(enquiryData?.DestinationList)
    ? enquiryData.DestinationList.map((item) => ({
        ...item,
        CountryName:
          item?.CountryName || item?.countryName || item?.label || "",
        Id: item?.Id ?? item?.id ?? item?.value ?? item?.CountryName ?? "",
      })).filter((item) => item.CountryName && item.Id !== "")
    : [];

  const travelers = Array.isArray(enquiryData?.Travelers)
    ? enquiryData.Travelers
    : [];
  const memberCount = Math.min(
    HOME_PAGE_MAX_MEMBERS,
    Number(enquiryData?.memberCount || travelers.length || 0),
  );
  const travellerDetails = Array.from({ length: memberCount }, (_, idx) => {
    const traveler = travelers[idx] || {};
    return {
      id: `members-${idx + 1}`,
      label: `Member ${idx + 1}`,
      ageRange: "",
      relationId:
        idx === 0
          ? String(traveler?.relation || HOME_PAGE_SELF_RELATION_ID)
          : String(traveler?.relation || ""),
      ageValue: String(traveler?.age || ""),
    };
  });

  return {
    selectedCountries: destinationList,
    startDate: String(enquiryData?.StartDate || ""),
    endDate: String(enquiryData?.EndDate || ""),
    memberCount,
    travellerDetails,
    mobile: String(enquiryData?.MobileNo || ""),
    tripPlan: getTripPlanKeyFromEnquiry(enquiryData),
    studentTripDuration: String(enquiryData?.studentTripDuration || ""),
    mobileError: getMobileValidationError(enquiryData?.MobileNo || ""),
  };
};

const tripPlanOptions = [
  { key: "single", value: "Single" },
  { key: "annualMultiTrip", value: "Annual Multi Trip" },
  { key: "student", value: "Student" },
];

const defaultStudentTripDurationOptions = Array.from(
  { length: 30 },
  (_, i) => ({
    key: i + 1,
    value: `${i + 1} day${i === 0 ? "" : "s"}`,
  }),
);

const getTripTypeForDurationApi = (tripPlan) => {
  if (tripPlan === "annualMultiTrip") return "MultiTrip";
  if (tripPlan === "student") return "Student";
  return "";
};

const normalizeDurationOptions = (durations) =>
  Array.isArray(durations)
    ? durations
        .map((item) => {
          const key = item?.Days ?? item?.key;
          const label =
            item?.DisplayText ??
            item?.label ??
            (key ? `${key} day${Number(key) === 1 ? "" : "s"}` : "");

          if (key === undefined || key === null || key === "") return null;
          return {
            key: String(key),
            value: String(label || key),
          };
        })
        .filter(Boolean)
    : [];

function CustomSelect({
  selectId,
  selectType,
  openSelect,
  setOpenSelect,
  value,
  placeholder,
  options,
  onChange,
  disabled = false,
}) {
  const isOpen = openSelect.id === selectId && openSelect.type === selectType;
  const selectedLabel =
    options.find((o) => String(o.key) === String(value))?.value || "";

  return (
    <div className="ti-select ti-select--form">
      <button
        type="button"
        disabled={disabled}
        className={`ti-select-btn${isOpen ? " is-open" : ""}`}
        onClick={() => {
          if (disabled) return;
          setOpenSelect((p) =>
            p.id === selectId && p.type === selectType
              ? { id: "", type: "" }
              : { id: selectId, type: selectType },
          );
        }}
      >
        <span>{selectedLabel || placeholder}</span>
        <span className="ti-select-caret">
          {isOpen ? <MdArrowDropUp /> : <IoMdArrowDropdown />}
        </span>
      </button>
      {isOpen && (
        <div className="ti-select-menu">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`ti-select-item${
                String(o.key) === String(value) ? " is-selected" : ""
              }`}
              onClick={() => {
                onChange(o.key);
                setOpenSelect({ id: "", type: "" });
              }}
            >
              {o.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DatePicker({
  value,
  onChange,
  minDate,
  placeholder,
  name,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => parseYMD(value) || new Date());
  const [panelPlacement, setPanelPlacement] = useState("bottom"); // "bottom" | "top"
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (value) setViewDate(parseYMD(value) || new Date());
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;

    const recalc = () => {
      const wrapEl = wrapRef.current;
      if (!wrapEl) return;

      const wrapRect = wrapEl.getBoundingClientRect();
      const viewportH = window.innerHeight || 0;
      const gap = 12; // panel gap + a little breathing room

      const panelH =
        panelRef.current?.getBoundingClientRect().height ||
        panelRef.current?.offsetHeight ||
        340;

      const spaceBelow = viewportH - wrapRect.bottom;
      const spaceAbove = wrapRect.top;

      if (spaceBelow >= panelH + gap) {
        setPanelPlacement("bottom");
        return;
      }
      if (spaceAbove >= panelH + gap) {
        setPanelPlacement("top");
        return;
      }

      setPanelPlacement(spaceAbove > spaceBelow ? "top" : "bottom");
    };

    const raf = requestAnimationFrame(recalc);
    window.addEventListener("resize", recalc);
    // capture scroll events from any scrollable parent (mobile browsers especially)
    window.addEventListener("scroll", recalc, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const min = parseYMD(minDate);
  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const isDisabled = (d) => {
    if (!min || !d) return false;
    const t = new Date(viewYear, viewMonth, d).getTime();
    const m = new Date(
      min.getFullYear(),
      min.getMonth(),
      min.getDate(),
    ).getTime();
    return t < m;
  };

  const selectDate = (d) => {
    if (!d || isDisabled(d)) return;
    const next = toYMD(new Date(viewYear, viewMonth, d));
    onChange(next);
    setOpen(false);
  };

  const monthLabel = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  }).format(new Date(viewYear, viewMonth, 1));

  const today = new Date();
  const isToday = (d) =>
    d &&
    viewYear === today.getFullYear() &&
    viewMonth === today.getMonth() &&
    d === today.getDate();

  const isSelected = (d) => {
    if (!d || !value) return false;
    const sel = parseYMD(value);
    return (
      sel &&
      sel.getFullYear() === viewYear &&
      sel.getMonth() === viewMonth &&
      sel.getDate() === d
    );
  };

  return (
    <div className="ti-date" ref={wrapRef}>
      {name && <input type="hidden" name={name} value={value || ""} />}
      <button
        type="button"
        className={`ti-date-input${open ? " is-open" : ""}`}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((p) => !p);
        }}
      >
        <span className={`ti-date-value${value ? " is-set" : ""}`}>
          {value ? formatPretty(value) : placeholder}
        </span>
        <span className="ti-date-icon">
          <CiCalendar />
        </span>
      </button>
      {open && !disabled && (
        <div
          ref={panelRef}
          className={`ti-date-panel${
            panelPlacement === "top" ? " ti-date-panel--top" : ""
          }`}
        >
          <div className="ti-date-header">
            <button
              type="button"
              className="ti-date-nav"
              onClick={() => setViewDate(new Date(viewYear, viewMonth - 1, 1))}
            >
              <FaAngleLeft />
            </button>
            <div className="ti-date-title">{monthLabel}</div>
            <button
              type="button"
              className="ti-date-nav"
              onClick={() => setViewDate(new Date(viewYear, viewMonth + 1, 1))}
            >
              <FaAngleRight />
            </button>
          </div>
          <div className="ti-date-week">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="ti-date-grid">
            {cells.map((d, i) => (
              <button
                key={`${d ?? "x"}-${i}`}
                type="button"
                className={`ti-date-cell${
                  d && isSelected(d) ? " is-selected" : ""
                }${d && isToday(d) ? " is-today" : ""}${
                  d && isDisabled(d) ? " is-disabled" : ""
                }`}
                onClick={() => selectDate(d)}
                disabled={!d || isDisabled(d)}
              >
                {d || ""}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mastersData = useSelector((state) => state.MastersData);
  const enquiryState = useSelector((state) => state.Enquiry);
  const enquiryData = enquiryState?.EnquiryData || null;
  const initialEnquiryState = getHomePageEnquiryState(enquiryData);

  useEffect(() => {
    dispatch(resetQuoteDetails());
    dispatch(resetTravellerDetails());
    dispatch(resetMastersData());
  }, [dispatch]);

  const SELF_RELATION_ID = HOME_PAGE_SELF_RELATION_ID;
  const MAX_MEMBERS = HOME_PAGE_MAX_MEMBERS;
  const MIN_ADULT_AGE = 18;
  const MAX_CHILDREN = 2;
  const [selectedCountries, setSelectedCountries] = useState(
    () => initialEnquiryState.selectedCountries,
  );
  // Stores full country objects: { CountryName, Id, CategoryCode, CategoryType, CountryCode }
  const [countryOptions, setCountryOptions] = useState([]);
  const [popularCountryOptions, setPopularCountryOptions] = useState([]);
  const [popularLoading, setPopularLoading] = useState(true);
  const [relationList, setRelationList] = useState([]);
  const [ageList, setAgeList] = useState([]);
  const [durationDaysList, setDurationDaysList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState(
    () => initialEnquiryState.startDate,
  );
  const [endDate, setEndDate] = useState(() => initialEnquiryState.endDate);
  const [memberCount, setMemberCount] = useState(
    () => initialEnquiryState.memberCount,
  );
  const [travellerOpen, setTravellerOpen] = useState(false);
  const [travellerDetails, setTravellerDetails] = useState(
    () => initialEnquiryState.travellerDetails,
  );
  const [memberCountDraft, setMemberCountDraft] = useState(
    () => initialEnquiryState.memberCount,
  );
  const [travellerDetailsDraft, setTravellerDetailsDraft] = useState(
    () => initialEnquiryState.travellerDetails,
  );
  const [mobile, setMobile] = useState(() => initialEnquiryState.mobile);
  const [mobileError, setMobileError] = useState(
    () => initialEnquiryState.mobileError,
  );
  const [viewQuotesConsent, setViewQuotesConsent] = useState(false);
  const [tripPlan, setTripPlan] = useState(() => initialEnquiryState.tripPlan);
  const [studentTripDuration, setStudentTripDuration] = useState(
    () =>
      initialEnquiryState.studentTripDuration ||
      (initialEnquiryState.tripPlan === "annualMultiTrip" ? "10" : ""),
  );
  const [openSelect, setOpenSelect] = useState({ id: "", type: "" });
  const dropdownRef = useRef(null);
  const travellerRef = useRef(null);
  const ageMenuRefs = useRef({});
  const relationMenuRefs = useRef({});
  const prevTripPlanRef = useRef(tripPlan);
  const lastManualDatesRef = useRef({ startDate: "", endDate: "" });
  const hasHydratedEnquiryRef = useRef(false);

  const maxMembersAllowed = tripPlan === "student" ? 1 : MAX_MEMBERS;
  const autoDateTrip = tripPlan === "student" || tripPlan === "annualMultiTrip";
  const activeTripDurationDays = autoDateTrip
    ? Number(studentTripDuration) || 0
    : 0;

  // Relation IDs that must be unique across members (Self/Spouse).
  // API names can vary, so we always include the required IDs too.
  const restrictedRelationIds = new Set([
    SELF_RELATION_ID, // Self
    263, // Spouse
    ...relationList
      .filter((r) => {
        const name = String(r.RelationName || "")
          .trim()
          .toLowerCase();
        return name.includes("self") || name.includes("spouse");
      })
      .map((r) => Number(r.RelationID)),
  ]);
  const spouseRelationIds = new Set([
    263, // Spouse
    ...relationList
      .filter((r) => {
        const name = String(r.RelationName || "")
          .trim()
          .toLowerCase();
        return name.includes("spouse");
      })
      .map((r) => Number(r.RelationID)),
  ]);
  const childRelationIds = new Set(
    relationList
      .filter((r) => {
        const name = String(r.RelationName || "")
          .trim()
          .toLowerCase();
        return (
          name.includes("child") ||
          name.includes("son") ||
          name.includes("daughter")
        );
      })
      .map((r) => Number(r.RelationID)),
  );
  const getAgeOptionsForTraveller = (index) =>
    index === 0 ? ageList.filter((a) => Number(a.AgeValue) >= 18) : ageList;

  // Filter by CountryName, exclude already selected (dedupe by Id)
  const filtered = countryOptions.filter(
    (c) =>
      c.CountryName.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !selectedCountries.some((s) => s.Id === c.Id),
  );
  const popularVisible = popularCountryOptions;

  const total = memberCount;

  const tripDays = (() => {
    if (autoDateTrip) return activeTripDurationDays || null;
    if (!startDate || !endDate) return null;
    const d = (new Date(endDate) - new Date(startDate)) / 86400000;
    if (d < 0) return null;
    return d + 1;
  })();

  const { adultCount, childCount } = travellerDetails.reduce(
    (acc, t) => {
      const age = Number(t.ageValue);
      if (!Number.isFinite(age)) return acc;
      if (age >= 18) acc.adultCount += 1;
      else acc.childCount += 1;
      return acc;
    },
    { adultCount: 0, childCount: 0 },
  );

  // Derived counts from selected ages (>=18 => Adult, else Child)
  const travellers = { adults: adultCount, children: childCount, seniors: 0 };

  const travellerSummaryLegacy =
    total === 0
      ? "Select traveller"
      : `${total} Traveller${total !== 1 ? "s" : ""}` +
        (travellers.adults
          ? ` · ${travellers.adults} Adult${travellers.adults > 1 ? "s" : ""}`
          : "") +
        (travellers.children
          ? ` · ${travellers.children} Child${travellers.children > 1 ? "ren" : ""}`
          : "") +
        (travellers.seniors
          ? ` · ${travellers.seniors} Senior${travellers.seniors > 1 ? "s" : ""}`
          : "");
  const travellerSummary =
    total === 0
      ? "Select traveller"
      : `${total} Traveller${total !== 1 ? "s" : ""}`;

  const allTravellerDetailsComplete =
    travellerDetails.length > 0 &&
    travellerDetails.every((t) => t.ageValue && t.relationId);
  const allTravellerDetailsCompleteDraft =
    travellerDetailsDraft.length > 0 &&
    travellerDetailsDraft.every((t) => t.ageValue && t.relationId);

  // Source of truth for button enablement is local state (Formik values are synced and can lag by a render).
  const canSubmit =
    !!tripPlan &&
    (!autoDateTrip || !!studentTripDuration) &&
    selectedCountries.length > 0 &&
    !!startDate &&
    !!endDate &&
    !!mobile &&
    !mobileError &&
    memberCount > 0 &&
    allTravellerDetailsComplete &&
    viewQuotesConsent;

  useEffect(() => {
    if (!autoDateTrip) {
      lastManualDatesRef.current = { startDate, endDate };
    }
  }, [autoDateTrip, startDate, endDate]);

  useEffect(() => {
    const prev = prevTripPlanRef.current;

    if (autoDateTrip && prev !== tripPlan) {
      const today = toYMD(new Date());
      setStartDate(today);
      const duration =
        tripPlan === "annualMultiTrip" ? 10 : Number(studentTripDuration) || 0;
      if (tripPlan === "annualMultiTrip") {
        setStudentTripDuration("10");
      }
      setEndDate(duration > 0 ? addDaysYMD(today, duration) : "");
    }

    if (!autoDateTrip && (prev === "student" || prev === "annualMultiTrip")) {
      const { startDate: prevStart, endDate: prevEnd } =
        lastManualDatesRef.current || {};
      setStartDate(prevStart || "");
      setEndDate(prevEnd || "");
    }

    prevTripPlanRef.current = tripPlan;
  }, [tripPlan, autoDateTrip, activeTripDurationDays]);

  useEffect(() => {
    if (!autoDateTrip) return;
    const today = toYMD(new Date());
    if (startDate !== today) setStartDate(today);

    if (activeTripDurationDays > 0) {
      setEndDate(addDaysYMD(today, activeTripDurationDays));
    } else {
      setEndDate("");
    }
  }, [autoDateTrip, activeTripDurationDays]); // intentionally not depending on startDate

  useEffect(() => {
    if (!autoDateTrip) return;
    if (memberCount <= 1) return;
    toast.warn("Student trip plan allows only 1 traveller");
    setMemberCount(1);
    setTravellerDetails((prev) => normalizeTravellerDetails(1, prev));
  }, [autoDateTrip, memberCount]);

  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
      if (travellerRef.current && !travellerRef.current.contains(e.target))
        setTravellerOpen(false);
      if (
        openSelect.id &&
        !(e.target && e.target.closest && e.target.closest(".ti-select"))
      ) {
        setOpenSelect({ id: "", type: "" });
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openSelect.id]);

  useEffect(() => {
    if (!travellerOpen) setOpenSelect({ id: "", type: "" });
  }, [travellerOpen]);

  useEffect(() => {
    if (openSelect.type !== "age" || !openSelect.id) return;
    const menuEl = ageMenuRefs.current[openSelect.id];
    if (menuEl) {
      const target = menuEl.querySelector('[data-age="18"]');
      if (target?.scrollIntoView) {
        target.scrollIntoView({ block: "center" });
      }
    }
  }, [openSelect]);

  useEffect(() => {
    if (openSelect.type !== "relation" || !openSelect.id) return;
    const menuEl = relationMenuRefs.current[openSelect.id];
    if (!menuEl) return;

    const selected =
      menuEl.querySelector(".is-selected") || menuEl.firstElementChild;
    if (selected?.scrollIntoView) {
      selected.scrollIntoView({ block: "center" });
    }
  }, [openSelect]);

  useEffect(() => {
    if (hasHydratedEnquiryRef.current || !enquiryData) return;

    const hasLocalContent =
      selectedCountries.length > 0 ||
      !!startDate ||
      !!endDate ||
      memberCount > 0 ||
      travellerDetails.length > 0 ||
      !!mobile ||
      !!tripPlan ||
      !!studentTripDuration;

    if (hasLocalContent) return;

    const hydrated = getHomePageEnquiryState(enquiryData);
    setSelectedCountries(hydrated.selectedCountries);
    setStartDate(hydrated.startDate);
    setEndDate(hydrated.endDate);
    setMemberCount(hydrated.memberCount);
    setTravellerDetails(hydrated.travellerDetails);
    setMemberCountDraft(hydrated.memberCount);
    setTravellerDetailsDraft(hydrated.travellerDetails);
    setMobile(hydrated.mobile);
    setMobileError(hydrated.mobileError);
    setTripPlan(hydrated.tripPlan);
    setStudentTripDuration(hydrated.studentTripDuration);
    hasHydratedEnquiryRef.current = true;
  }, [
    enquiryData,
    selectedCountries.length,
    startDate,
    endDate,
    memberCount,
    travellerDetails,
    mobile,
    tripPlan,
    studentTripDuration,
  ]);

  // Toggle a full country object in/out of selectedCountries
  const toggleCountry = (countryObj) => {
    setSelectedCountries((prev) =>
      prev.some((s) => s.Id === countryObj.Id)
        ? prev.filter((s) => s.Id !== countryObj.Id)
        : [...prev, countryObj],
    );
    setSearchQuery("");
  };

  const normalizeTravellerDetails = (count, prevDetails) => {
    const safeCount = Math.min(MAX_MEMBERS, Math.max(0, Number(count) || 0));
    const next = (prevDetails || []).slice(0, safeCount).map((t) => ({
      relationId: t.relationId || "",
      ageValue: t.ageValue || "",
    }));

    while (next.length < safeCount) {
      next.push({ relationId: "", ageValue: "" });
    }

    return next.map((t, idx) => ({
      id: `members-${idx + 1}`,
      label: `Member ${idx + 1}`,
      ageRange: "",
      relationId: idx === 0 ? SELF_RELATION_ID : t.relationId,
      ageValue: t.ageValue,
    }));
  };

  useEffect(() => {
    setTravellerDetails((prev) => normalizeTravellerDetails(memberCount, prev));
  }, [memberCount]);

  const updateRelationDraft = (id, relationId) => {
    const nextId = relationId ? Number(relationId) : "";
    setTravellerDetailsDraft((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === 0) return prev;
      const rawAge = prev[idx]?.ageValue;
      const currentAge =
        rawAge === "" || rawAge === null || rawAge === undefined
          ? null
          : Number(rawAge);

      return prev.map((t) => {
        if (t.id !== id) return t;
        if (
          nextId &&
          spouseRelationIds.has(nextId) &&
          Number.isFinite(currentAge) &&
          currentAge < MIN_ADULT_AGE
        ) {
          toast.error("Spouse relation requires age 18 or above");
          return t;
        }
        if (
          nextId &&
          childRelationIds.has(nextId) &&
          prev.filter(
            (x) =>
              x.id !== id && childRelationIds.has(Number(x.relationId) || 0),
          ).length >= MAX_CHILDREN
        ) {
          toast.error("Maximum 2 children allowed");
          return t;
        }
        if (
          nextId &&
          restrictedRelationIds.has(nextId) &&
          prev.some((x) => x.id !== id && Number(x.relationId) === nextId)
        ) {
          return t;
        }
        return { ...t, relationId: nextId };
      });
    });
  };

  const updateAgeDraft = (id, ageValue) => {
    const nextAge = Number(ageValue);
    setTravellerDetailsDraft((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1) return prev;

      if (Number.isFinite(nextAge) && nextAge < MIN_ADULT_AGE) {
        if (idx === 0) {
          toast.error("Member 1 age must be 18 or above");
          return prev;
        }
        const relId = Number(prev[idx]?.relationId) || 0;
        if (relId && spouseRelationIds.has(relId)) {
          toast.error("Spouse age must be 18 or above");
          return prev;
        }
        const adultExists = prev.some((t, i) => {
          if (i === idx) return false;
          const a = Number(t.ageValue);
          return Number.isFinite(a) && a >= MIN_ADULT_AGE;
        });
        if (!adultExists) {
          toast.error("Add an adult (18+) first");
          return prev;
        }
        const otherChildrenCount = prev.filter((t) => {
          if (t.id === id) return false;
          const a = Number(t.ageValue);
          return Number.isFinite(a) && a < MIN_ADULT_AGE;
        }).length;
        if (otherChildrenCount >= MAX_CHILDREN) {
          toast.error("Maximum 2 children allowed");
          return prev;
        }
      }

      return prev.map((t) => (t.id === id ? { ...t, ageValue } : t));
    });
  };

  const adjustDraft = (delta) => {
    setMemberCountDraft((p) => {
      const rawNext = p + delta;
      const next = Math.min(maxMembersAllowed, Math.max(0, rawNext));
      if (rawNext > maxMembersAllowed) {
        if (tripPlan === "student")
          toast.warn("Student trip plan allows only 1 traveller");
        else toast.error("Maximum 4 members allowed");
      }
      setTravellerDetailsDraft((prev) => normalizeTravellerDetails(next, prev));
      return next;
    });
  };

  const removeTravellerDraft = (id) => {
    setTravellerDetailsDraft((prev) => {
      if ((prev || [])[0]?.id === id) return prev;
      const remaining = (prev || []).filter((t) => t.id !== id);
      const nextCount = Math.max(0, remaining.length);
      setMemberCountDraft(nextCount);
      return normalizeTravellerDetails(nextCount, remaining);
    });
  };

  const closeTravellerModal = () => {
    setTravellerOpen(false);
  };

  const applyTravellerModal = () => {
    if (!allTravellerDetailsCompleteDraft) return;
    const nextCount = Math.min(maxMembersAllowed, memberCountDraft);

    if (tripPlan === "student" && memberCountDraft > 1) {
      toast.warn("Student trip plan allows only 1 traveller");
    }
    setMemberCount(nextCount);
    setTravellerDetails(
      normalizeTravellerDetails(nextCount, travellerDetailsDraft),
    );
    setTravellerOpen(false);
  };

  useEffect(() => {
    if (travellerOpen) {
      const isFirstOpenEmpty =
        memberCount === 0 &&
        (!travellerDetails || travellerDetails.length === 0);

      const nextCount = Math.min(
        maxMembersAllowed,
        isFirstOpenEmpty ? 1 : memberCount,
      );

      const nextDetails = normalizeTravellerDetails(
        nextCount,
        isFirstOpenEmpty ? [] : travellerDetails,
      );

      // Default preselect: 1 member with Self (RelationID 69) + Age 18 (AgeValue 18).
      if (isFirstOpenEmpty && nextDetails[0]) {
        nextDetails[0] = {
          ...nextDetails[0],
          relationId: SELF_RELATION_ID,
          ageValue: 18,
        };
      }

      setMemberCountDraft(nextCount);
      setTravellerDetailsDraft(nextDetails);
    }
  }, [travellerOpen, tripPlan]);

  const validateMobile = (v) => {
    setMobileError(getMobileValidationError(v));
  };

  const todayYMD = toYMD(new Date());

  const validationSchema = Yup.object({
    destinations: Yup.array().min(1, "Required").required("Required"),
    startDate: Yup.string().required("Required"),
    endDate: Yup.string().required("Required"),
    mobile: Yup.string()
      .matches(/^[6-9]\d{9}$/, "Invalid mobile")
      .required("Required"),
    members: Yup.number().min(1, "Required").required("Required"),
    travellerDetailsComplete: Yup.boolean()
      .oneOf([true], "Required")
      .required("Required"),
    viewQuotesConsent: Yup.boolean()
      .oneOf([true], "Required")
      .required("Required"),
  });

  const SyncFormik = ({ setFieldValue, validateForm }) => {
    useEffect(() => {
      setFieldValue("destinations", selectedCountries, true);
      setFieldValue("startDate", startDate, true);
      setFieldValue("endDate", endDate, true);
      setFieldValue("mobile", mobile, true);
      setFieldValue("members", memberCount, true);
      setFieldValue("tripPlan", tripPlan, true);
      setFieldValue("tripDurationDays", studentTripDuration, true);
      setFieldValue(
        "travellerDetailsComplete",
        allTravellerDetailsComplete,
        true,
      );
      setFieldValue("viewQuotesConsent", viewQuotesConsent, true);
      if (validateForm) validateForm();
    }, [
      selectedCountries,
      startDate,
      endDate,
      mobile,
      memberCount,
      tripPlan,
      studentTripDuration,
      allTravellerDetailsComplete,
      viewQuotesConsent,
      setFieldValue,
      validateForm,
    ]);

    return null;
  };

  const buildEnquiryPayload = () => {
    const travellersPayload = travellerDetails.map((t) => ({
      dob: "",
      age: Number(t.ageValue) || 0,
      relation: Number(t.relationId) || 0,
    }));
    const destinationList = selectedCountries.map((c) => ({
      ...c,
      label: c.CountryName,
      value: c.Id,
    }));

    return {
      StartDate: startDate,
      EndDate: endDate,
      UserId: 0,
      BusinessID: "",
      LeadSource: "online",
      MacId: "web",
      MobileNo: mobile,
      numberofDays: tripDays || 0,
      Travelers: travellersPayload,
      DestinationList: destinationList,
    };
  };

  const buildReduxEnquiryData = (payload) => ({
    ...payload,
    tripPlan,
    tripType:
      tripPlan === "annualMultiTrip"
        ? "Annual Multi Trip"
        : tripPlan === "student"
          ? "Student"
          : "Single",
    studentTripDuration: tripPlan === "student" ? studentTripDuration : "",
    memberCount,
    policyType: getPolicyType(
      tripPlan === "annualMultiTrip"
        ? "Annual Multi Trip"
        : tripPlan === "student"
          ? "Student"
          : "Single",
      payload.Travelers?.length || 0,
    ),
  });

  const fetchMasterList = async ({
    apiUrl,
    cacheState,
    setState,
    dispatchKey,
  }) => {
    try {
      if (mastersData?.[cacheState]?.length > 0) {
        setState(mastersData[cacheState]);
        return;
      }

      const model = {
        URL: apiUrl,
      };

      const Res = await API.CallRequestWithHeader(model);
      const response = Array.isArray(Res?.Response) ? Res.Response : [];
      setState(response);
      dispatch(updateMastersData({ [dispatchKey]: response }));
    } catch (error) {
      console.log(error);
    }
  };

  // ── Fetch countries from API ──────────────────────────────────────────────
  const getCountryMaster = async () => {
    try {
      if (mastersData?.countryList?.length > 0) {
        setCountryOptions(mastersData.countryList);
        return;
      }
      const model = {
        URL: "GetCountries",
        PostString: JSON.stringify([]),
      };

      const Res = await API.CallRequestWithHeader(model);

      // Res is the array: [{ CountryName, Id, CategoryCode, CategoryType, CountryCode, ErrorMessage }]
      if (Res?.Response?.length > 0) {
        const mapped = Res.Response.map((c) => ({
          ...c,
          label: c.CountryName,
          value: c.Id,
        }));
        setCountryOptions(mapped);
        dispatch(updateMastersData({ countryList: mapped }));
      }
    } catch (error) {
      console.log(error);
    }
  };
  const getPopularCountryMaster = async () => {
    try {
      if (mastersData?.popularCountries?.length > 0) {
        setPopularCountryOptions(mastersData.popularCountries);
        setPopularLoading(false);
        return;
      }
      setPopularLoading(true);
      const model = {
        URL: "GetPopularCountries",
        PostString: JSON.stringify([]),
      };

      const Res = await API.CallRequestWithHeader(model);

      // Res is the array: [{ CountryName, Id, CategoryCode, CategoryType, CountryCode, ErrorMessage }]
      if (Res?.Response?.length > 0) {
        const mapped = Res.Response.map((c) => ({
          ...c,
          label: c.CountryName,
          value: c.Id,
        }));
        setPopularCountryOptions(mapped);
        dispatch(updateMastersData({ popularCountries: mapped }));
      } else {
        setPopularCountryOptions([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPopularLoading(false);
    }
  };
  const getRelationMaster = async () => {
    await fetchMasterList({
      cacheState: "relationList",
      apiUrl: "GetRelationTypeList",
      setState: setRelationList,
      dispatchKey: "relationList",
    });
  };
  const getAgeMaster = async () => {
    await fetchMasterList({
      cacheState: "ageList",
      apiUrl: "GetAgeItemList",
      setState: setAgeList,
      dispatchKey: "ageList",
    });
  };

  const getDurationDaysMaster = async () => {
    try {
      const model = {
        URL: "GetDurationDays",
      };

      const Res = await API.CallRequestWithHeader(model);
      const response = Array.isArray(Res?.Response) ? Res.Response : [];
      setDurationDaysList(response);
      dispatch(updateMastersData({ durationDaysList: response }));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getRelationMaster();
    getAgeMaster();
    getDurationDaysMaster();
    getCountryMaster();
    getPopularCountryMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const durationTripType = getTripTypeForDurationApi(tripPlan);
  const durationApiGroup = durationDaysList.find(
    (item) =>
      String(item?.TripType || "").toLowerCase() ===
      durationTripType.toLowerCase(),
  );

  const tripDurationOptions = normalizeDurationOptions(
    durationApiGroup?.Durations,
  );
  const studentTripDurationOptions =
    tripDurationOptions.length > 0
      ? tripDurationOptions
      : defaultStudentTripDurationOptions;

  useEffect(() => {
    if (!autoDateTrip || studentTripDurationOptions.length === 0) return;

    const currentValue = String(studentTripDuration || "");
    const hasCurrentValue = studentTripDurationOptions.some(
      (option) => String(option.key) === currentValue,
    );

    if (!hasCurrentValue) {
      setStudentTripDuration(String(studentTripDurationOptions[0].key));
    }
  }, [autoDateTrip, studentTripDuration, studentTripDurationOptions]);

  return (
    <div className="ti-root">
      <div className="ti-split">
        {/* ────────── LEFT · HERO ────────── */}
        <div className="ti-hero">
          <div className="ti-hero-orb ti-hero-orb--1" />
          <div className="ti-hero-orb ti-hero-orb--2" />

          <div className="ti-logo">
            <span className="ti-logo-plane">✈</span>
            <span className="ti-logo-name">
              SafeJourney<span className="ti-logo-dot">.</span>
            </span>
          </div>

          <div className="ti-live-badge">
            <span className="ti-live-dot" />
            Trusted by 2 Crore+ Travellers
          </div>

          <h3 className="ti-hero-title">
            Travel Smart,
            <br />
            <em className="ti-hero-em">Travel Insured</em>
          </h3>

          <p className="ti-hero-sub">
            Comprehensive coverage across 180+ countries. Get your policy in
            under 2 minutes.
          </p>

          <ul className="ti-perks">
            {[
              ["🏥", "Medical cover up to ₹1.5 Crore"],
              ["✈️", "Trip cancellation & curtailment"],
              ["🎒", "Baggage & passport protection"],
              ["📞", "24 / 7 multilingual helpline"],
            ].map(([icon, text]) => (
              <li key={text} className="ti-perk">
                <span className="ti-perk-icon">{icon}</span>
                {text}
              </li>
            ))}
          </ul>

          <div className="ti-insurers">
            {["Iddusland General","Bajaj General"].map(
              (n) => (
                <span key={n} className="ti-insurer">
                  {n}
                </span>
              ),
            )}
          </div>
        </div>

        {/* ────────── RIGHT · FORM ────────── */}
        <div className="ti-form-side">
          <div className="ti-card">
            <div className="ti-card-head">
              <h2 className="ti-card-title">Where are you travelling to?</h2>
            </div>

            <Formik
              initialValues={{
                destinations: selectedCountries,
                destinationSearch: "",
                startDate,
                endDate,
                members: memberCount,
                mobile,
                tripPlan,
                tripDurationDays: studentTripDuration,
                travellerDetailsComplete: false,
                viewQuotesConsent: false,
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, actions) => {
                try {
                  const payload = buildEnquiryPayload();
                  const model = {
                    URL: "GenerateEnquiry",
                    PostString: JSON.stringify(payload),
                  };
                  const res = await API.CallRequestWithHeader(model);
                  if (res?.StatusCode === 200) {
                    dispatch(setEnquiryData(buildReduxEnquiryData(payload)));
                    if (res?.Response) {
                      dispatch(setEnquiryMeta(res.Response));
                    }

                    // Keep the existing quotation/edit flows working by syncing the same data into TravellerDetails.
                    dispatch(
                      updateTravellerDetails({
                        mobileNo: payload.MobileNo,
                        startDate: payload.StartDate,
                        endDate: payload.EndDate,
                        numberOfDays: payload.numberofDays,
                        destination: payload.DestinationList,
                        travellers: payload.Travelers,
                        enquiryNo: res?.Response?.EnquiryNo || "",
                        lotNo: res?.Response?.LotNo || "",
                        tripType:
                          tripPlan === "annualMultiTrip"
                            ? "Annual Multi Trip"
                            : tripPlan === "student"
                              ? "Student"
                              : "Single",
                        policyType: getPolicyType(
                          tripPlan === "annualMultiTrip"
                            ? "Annual Multi Trip"
                            : tripPlan === "student"
                              ? "Student"
                              : "Single",
                          payload.Travelers?.length || 0,
                        ),
                      }),
                    );
                    navigate("/Quotation");
                  } else {
                    toast.error(res?.Message || "Some error occured");
                  }
                } catch (error) {
                  console.log(error);
                  toast.error("Some error occured");
                } finally {
                  actions.setSubmitting(false);
                }
              }}
              validateOnMount
            >
              {({
                handleSubmit,
                setFieldValue,
                validateForm,
                isSubmitting,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <SyncFormik
                    setFieldValue={setFieldValue}
                    validateForm={validateForm}
                  />
                  <div className="ti-grid">
                    {/* Destination */}
                    <div className="ti-field ti-field--full" ref={dropdownRef}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <label className="ti-label">
                          Travel Destination(s)
                          <span className="ti-required">*</span>
                        </label>
                        {selectedCountries.length > 0 && (
                          <span className="ti-count">
                            {selectedCountries.length} destination(s) selected
                          </span>
                        )}
                      </div>
                      <div
                        className={`ti-multiselect${dropdownOpen ? " is-open" : ""}`}
                        onClick={() => setDropdownOpen(true)}
                      >
                        <div className="ti-tags">
                          {selectedCountries.map((c) => (
                            <span key={c.Id} className="ti-tag">
                              <FlagImg countryName={c.CountryName} size={16} />
                              &nbsp;{c.CountryName}
                              <button
                                className="ti-tag-x"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCountries((prev) =>
                                    prev.filter((s) => s.Id !== c.Id),
                                  );
                                }}
                              >
                                <RxCross1 size={9} />
                              </button>
                            </span>
                          ))}
                          <input
                            name="destinationSearch"
                            className="ti-tag-input"
                            placeholder={
                              selectedCountries.length === 0
                                ? "Search & select destinations..."
                                : "Add more..."
                            }
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              setDropdownOpen(true);
                            }}
                            onFocus={() => setDropdownOpen(true)}
                          />
                        </div>
                        <span className="ti-chevron">
                          {dropdownOpen ? (
                            <MdArrowDropUp size={15} />
                          ) : (
                            <IoMdArrowDropdown size={15} />
                          )}
                        </span>
                      </div>

                      {popularLoading && (
                        <div className="ti-popular">
                          <div className="ti-popular-title">
                            Popular countries
                          </div>
                          <div className="ti-popular-list">
                            {Array.from({ length: 8 }).map((_, i) => (
                              <div key={i} className="ti-skeleton-chip" />
                            ))}
                          </div>
                        </div>
                      )}

                      {!popularLoading && popularVisible.length > 0 && (
                        <div className="ti-popular">
                          <div className="ti-popular-title">
                            Popular countries
                          </div>
                          <div className="ti-popular-list">
                            {popularVisible.map((c) => (
                              <button
                                key={c.Id}
                                type="button"
                                className={`ti-popular-chip${
                                  selectedCountries.some((s) => s.Id === c.Id)
                                    ? " is-selected"
                                    : ""
                                }`}
                                onClick={() => toggleCountry(c)}
                              >
                                <FlagImg
                                  countryName={c.CountryName}
                                  size={14}
                                />
                                <span>{c.CountryName}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {dropdownOpen && (
                        <div className="ti-dropdown">
                          {filtered.length === 0 ? (
                            <p className="ti-dropdown-empty">
                              No countries found
                            </p>
                          ) : (
                            filtered.map((c) => (
                              <div
                                key={c.Id}
                                className={`ti-option${
                                  selectedCountries.some((s) => s.Id === c.Id)
                                    ? " is-selected"
                                    : ""
                                }`}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  toggleCountry(c);
                                }}
                              >
                                <FlagImg
                                  countryName={c.CountryName}
                                  size={20}
                                />
                                <span>{c.CountryName}</span>
                                {selectedCountries.some(
                                  (s) => s.Id === c.Id,
                                ) && <span className="ti-tick">check</span>}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Trip plan + Travellers */}
                    <div className="ti-two-col">
                      <input type="hidden" name="tripPlan" value={tripPlan} />
                      <input
                        type="hidden"
                        name="tripDurationDays"
                        value={studentTripDuration}
                      />

                      <div className="ti-field">
                        <label className="ti-label">
                          Trip Plan
                          <span className="ti-required">*</span>
                        </label>
                        <CustomSelect
                          selectId="trip"
                          selectType="plan"
                          openSelect={openSelect}
                          setOpenSelect={setOpenSelect}
                          value={tripPlan}
                          placeholder="Select plan"
                          options={tripPlanOptions}
                          onChange={(nextPlan) => {
                            setTripPlan(String(nextPlan));
                            if (String(nextPlan) !== "student")
                              setStudentTripDuration("");
                          }}
                        />
                      </div>

                      <input
                        type="hidden"
                        name="destinations"
                        value={selectedCountries.map((c) => c.Id).join(",")}
                      />
                      <input type="hidden" name="adults" value={adultCount} />
                      <input type="hidden" name="children" value={childCount} />
                      <input type="hidden" name="seniors" value={0} />
                      <div
                        className="ti-field ti-field--travellers"
                        ref={travellerRef}
                      >
                        <label className="ti-label">
                          Travellers
                          <span className="ti-required">*</span>
                        </label>
                        <div
                          className={`ti-input ti-input--select${travellerOpen ? " is-open" : ""}`}
                          onClick={() => setTravellerOpen(true)}
                        >
                          <span>{travellerSummary}</span>
                          <span className="ti-chevron">
                            {travellerOpen ? (
                              <MdArrowDropUp size={15} />
                            ) : (
                              <IoMdArrowDropdown size={15} />
                            )}
                          </span>
                        </div>
                        {memberCount > 0 && (
                          <div className="ti-traveller-breakdown">
                            {adultCount} Adult{adultCount !== 1 ? "s" : ""} ·{" "}
                            {childCount} Child
                            {childCount !== 1 ? "ren" : ""}
                          </div>
                        )}
                        {travellerOpen && (
                          <div className="ti-modal">
                            <button
                              type="button"
                              className="ti-modal-backdrop"
                              onClick={closeTravellerModal}
                              aria-label="Close travellers modal"
                            />
                            <div
                              className="ti-modal-card"
                              role="dialog"
                              aria-modal="true"
                            >
                              <div className="ti-modal-head">
                                <div className="ti-modal-title">Travellers</div>
                                <button
                                  type="button"
                                  className="ti-modal-close"
                                  onClick={closeTravellerModal}
                                >
                                  Close
                                </button>
                              </div>

                              <div className="ti-modal-scroll">
                                <div className="ti-modal-section">
                                  <div className="ti-trow ti-trow--members">
                                    <div>
                                      <div className="ti-tlabel">Members</div>
                                      <div className="ti-tsub">Max 4</div>
                                    </div>
                                    <div className="ti-counter ti-counter--right">
                                      <button
                                        type="button"
                                        className="ti-cbtn"
                                        onClick={() => adjustDraft(-1)}
                                        disabled={memberCountDraft <= 0}
                                      >
                                        <FaMinus />
                                      </button>
                                      <span className="ti-cval">
                                        {memberCountDraft}
                                      </span>
                                      <button
                                        type="button"
                                        className="ti-cbtn"
                                        onClick={() => adjustDraft(1)}
                                        disabled={
                                          memberCountDraft >= maxMembersAllowed
                                        }
                                      >
                                        <FaPlus />
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <div className="ti-modal-section mb-5">
                                  {travellerDetailsDraft.length > 0 && (
                                    <div className="ti-modal-subtitle">
                                      Traveller details
                                    </div>
                                  )}
                                  <div className="ti-modal-table">
                                    <div className="ti-modal-row ti-modal-row--head">
                                      <div className="ti-modal-cell" />
                                      {travellerDetailsDraft.length > 0 && (
                                        <>
                                          <div className="ti-modal-cell">
                                            Traveller
                                          </div>
                                          <div className="ti-modal-cell">
                                            Relation
                                          </div>
                                          <div className="ti-modal-cell">
                                            Age
                                          </div>
                                          <div className="ti-modal-cell" />
                                        </>
                                      )}
                                    </div>
                                    {travellerDetailsDraft.map((t, idx) => {
                                      const selfRelationName =
                                        relationList.find(
                                          (r) =>
                                            Number(r.RelationID) ===
                                            Number(SELF_RELATION_ID),
                                        )?.RelationName || "Self";
                                      const currentAge =
                                        t.ageValue === "" ||
                                        t.ageValue === null ||
                                        t.ageValue === undefined
                                          ? null
                                          : Number(t.ageValue);
                                      const blockSpouseRelation =
                                        Number.isFinite(currentAge) &&
                                        currentAge < MIN_ADULT_AGE;
                                      const selectedRestrictedIds = new Set(
                                        travellerDetailsDraft
                                          .filter((x) => x.id !== t.id)
                                          .map((x) => Number(x.relationId) || 0)
                                          .filter(
                                            (id) =>
                                              id &&
                                              restrictedRelationIds.has(id),
                                          ),
                                      );
                                      const availableRelations =
                                        relationList.filter(
                                          (r) =>
                                            !selectedRestrictedIds.has(
                                              Number(r.RelationID),
                                            ) ||
                                            Number(r.RelationID) ===
                                              Number(t.relationId),
                                        );

                                      return (
                                        <div
                                          key={t.id}
                                          className="ti-modal-row"
                                        >
                                          <div className="ti-modal-cell">
                                            <span className="ti-num">
                                              {idx + 1}
                                            </span>
                                          </div>
                                          <div className="ti-modal-cell">
                                            {t.label}
                                          </div>

                                          <div className="ti-modal-cell">
                                            <div className="ti-select">
                                              <button
                                                type="button"
                                                className={`ti-select-btn${
                                                  openSelect.id === t.id &&
                                                  openSelect.type === "relation"
                                                    ? " is-open"
                                                    : ""
                                                }`}
                                                disabled={idx === 0}
                                                onClick={() =>
                                                  setOpenSelect((p) =>
                                                    p.id === t.id &&
                                                    p.type === "relation"
                                                      ? { id: "", type: "" }
                                                      : {
                                                          id: t.id,
                                                          type: "relation",
                                                        },
                                                  )
                                                }
                                              >
                                                <span>
                                                  {idx === 0
                                                    ? selfRelationName
                                                    : t.relationId
                                                      ? availableRelations.find(
                                                          (r) =>
                                                            String(
                                                              r.RelationID,
                                                            ) ===
                                                            String(
                                                              t.relationId,
                                                            ),
                                                        )?.RelationName ||
                                                        "Select"
                                                      : "Select"}
                                                </span>
                                                {idx !== 0 && (
                                                  <span className="ti-select-caret">
                                                    {openSelect.id === t.id &&
                                                    openSelect.type ===
                                                      "relation" ? (
                                                      <MdArrowDropUp />
                                                    ) : (
                                                      <IoMdArrowDropdown />
                                                    )}
                                                  </span>
                                                )}
                                              </button>
                                              {idx !== 0 &&
                                                openSelect.id === t.id &&
                                                openSelect.type ===
                                                  "relation" && (
                                                  <div
                                                    className="ti-select-menu"
                                                    ref={(el) => {
                                                      relationMenuRefs.current[
                                                        t.id
                                                      ] = el;
                                                    }}
                                                  >
                                                    {availableRelations.map(
                                                      (r) => (
                                                        <button
                                                          type="button"
                                                          key={r.RelationID}
                                                          className={`ti-select-item${
                                                            String(
                                                              t.relationId,
                                                            ) ===
                                                            String(r.RelationID)
                                                              ? " is-selected"
                                                              : ""
                                                          }`}
                                                          disabled={
                                                            blockSpouseRelation &&
                                                            spouseRelationIds.has(
                                                              Number(
                                                                r.RelationID,
                                                              ),
                                                            )
                                                          }
                                                          onClick={() => {
                                                            if (
                                                              blockSpouseRelation &&
                                                              spouseRelationIds.has(
                                                                Number(
                                                                  r.RelationID,
                                                                ),
                                                              )
                                                            ) {
                                                              return;
                                                            }
                                                            updateRelationDraft(
                                                              t.id,
                                                              String(
                                                                r.RelationID,
                                                              ),
                                                            );
                                                            setOpenSelect({
                                                              id: "",
                                                              type: "",
                                                            });
                                                          }}
                                                        >
                                                          {r.RelationName}
                                                        </button>
                                                      ),
                                                    )}
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                          <div className="ti-modal-cell">
                                            <div className="ti-select">
                                              <button
                                                type="button"
                                                className={`ti-select-btn${
                                                  openSelect.id === t.id &&
                                                  openSelect.type === "age"
                                                    ? " is-open"
                                                    : ""
                                                }`}
                                                onClick={() =>
                                                  setOpenSelect((p) =>
                                                    p.id === t.id &&
                                                    p.type === "age"
                                                      ? { id: "", type: "" }
                                                      : {
                                                          id: t.id,
                                                          type: "age",
                                                        },
                                                  )
                                                }
                                              >
                                                <span>
                                                  {t.ageValue
                                                    ? getAgeOptionsForTraveller(
                                                        idx,
                                                      ).find(
                                                        (a) =>
                                                          String(a.AgeValue) ===
                                                          String(t.ageValue),
                                                      )?.AgeText || "Select"
                                                    : "Select"}
                                                </span>
                                                <span className="ti-select-caret">
                                                  {openSelect.id === t.id &&
                                                  openSelect.type === "age" ? (
                                                    <MdArrowDropUp />
                                                  ) : (
                                                    <IoMdArrowDropdown />
                                                  )}
                                                </span>
                                              </button>
                                              {openSelect.id === t.id &&
                                                openSelect.type === "age" && (
                                                  <div
                                                    className="ti-select-menu"
                                                    ref={(el) => {
                                                      ageMenuRefs.current[
                                                        t.id
                                                      ] = el;
                                                    }}
                                                  >
                                                    {getAgeOptionsForTraveller(
                                                      idx,
                                                    ).map((a) => (
                                                      <button
                                                        type="button"
                                                        key={a.AgeValue}
                                                        data-age={String(
                                                          a.AgeValue,
                                                        )}
                                                        className={`ti-select-item${
                                                          String(t.ageValue) ===
                                                          String(a.AgeValue)
                                                            ? " is-selected"
                                                            : ""
                                                        }`}
                                                        onClick={() => {
                                                          updateAgeDraft(
                                                            t.id,
                                                            String(a.AgeValue),
                                                          );
                                                          setOpenSelect({
                                                            id: "",
                                                            type: "",
                                                          });
                                                        }}
                                                      >
                                                        {a.AgeText}
                                                      </button>
                                                    ))}
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                          <div className="ti-modal-cell ti-modal-cell--actions">
                                            {idx !== 0 && (
                                              <button
                                                type="button"
                                                className="ti-remove"
                                                onClick={() =>
                                                  removeTravellerDraft(t.id)
                                                }
                                                aria-label={`Remove ${t.label}`}
                                              >
                                                <FaTrash />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>

                              <div className="ti-modal-actions">
                                <button
                                  type="button"
                                  className="ti-done"
                                  onClick={applyTravellerModal}
                                  disabled={!allTravellerDetailsCompleteDraft}
                                >
                                  Done
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date range */}
                    <div className="ti-date-range">
                      <div className="ti-field">
                        <label className="ti-label">
                          Start Date
                          <span className="ti-required">*</span>
                        </label>
                        <DatePicker
                          name="startDate"
                          value={startDate}
                          minDate={todayYMD}
                          placeholder="DD-MM-YYYY"
                          onChange={(v) => {
                            setStartDate(v);
                            if (autoDateTrip && activeTripDurationDays > 0) {
                              setEndDate(addDaysYMD(v, activeTripDurationDays));
                            } else if (endDate && v > endDate) {
                              setEndDate("");
                            }
                          }}
                        />
                      </div>

                      <div className="ti-date-between">
                        {tripDays ? (
                          <span className="ti-date-days">{tripDays}d</span>
                        ) : (
                          <span className="ti-date-connector" />
                        )}
                      </div>

                      <div className="ti-field">
                        <label className="ti-label ti-label--between">
                          <span>
                            End Date
                            <span className="ti-required">*</span>
                          </span>
                          {tripDays ? (
                            <span className="ti-date-days ti-date-days--mobile">
                              {tripDays}d
                            </span>
                          ) : null}
                        </label>
                        <DatePicker
                          name="endDate"
                          value={endDate}
                          minDate={startDate || todayYMD}
                          placeholder="DD-MM-YYYY"
                          disabled={autoDateTrip}
                          onChange={(v) => {
                            if (!autoDateTrip) setEndDate(v);
                          }}
                        />
                      </div>
                    </div>
                    {autoDateTrip && (
                      <div className="ti-field ti-field--full">
                        <label className="ti-label">
                          Duration of Trips
                          <span className="ti-required">*</span>
                        </label>
                        <CustomSelect
                          selectId="trip"
                          selectType="duration"
                          openSelect={openSelect}
                          setOpenSelect={setOpenSelect}
                          value={studentTripDuration}
                          placeholder="Select duration"
                          options={studentTripDurationOptions}
                          onChange={(v) => setStudentTripDuration(String(v))}
                        />
                      </div>
                    )}

                    <div className="ti-field ti-field--full">
                      <label className="ti-label">
                        Mobile Number
                        <span className="ti-required">*</span>
                      </label>
                      <div
                        className={`ti-mobile${mobileError ? " is-error" : ""}`}
                      >
                        <span className="ti-prefix">+91</span>
                        <input
                          name="mobile"
                          type="tel"
                          maxLength={10}
                          className="ti-mobile-input"
                          placeholder="10-digit mobile number"
                          value={mobile}
                          onChange={(e) => {
                            let v = e.target.value.replace(/\D/g, "");
                            if (v.length === 1 && !/[6-9]/.test(v)) return;
                            if (v.length >= 6 && /^(\d)\1{5}/.test(v)) return;
                            setMobile(v);
                            validateMobile(v);
                          }}
                        />
                      </div>
                      {mobileError && mobile.length === 10 && (
                        <span className="ti-error">{mobileError}</span>
                      )}
                    </div>
                  </div>

                  <div className="ti-consent">
                    <input
                      type="checkbox"
                      aria-label="Accept privacy policy, terms of use, and DND consent"
                      checked={viewQuotesConsent}
                      onChange={(e) => setViewQuotesConsent(e.target.checked)}
                    />
                    <div className="checkboxtitle">
                      By clicking <strong>View Quotes</strong>, you accept our{" "}
                      <a
                        href={`${policyBaseUrl}/corporate-governance-and-legal-policies`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                      ,{" "}
                      <a
                        href={`${policyBaseUrl}/corporate-governance-and-legal-policies#terms`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Use
                      </a>{" "}
                      and provide your consent for DND communications.
                    </div>
                  </div>
                  <button
                    className="ti-cta"
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="ti-cta-loading">
                        <span className="ti-spinner" />
                        Processing...
                      </span>
                    ) : (
                      <>
                        View Plans &amp; Pricing
                        <span className="ti-cta-arrow">→</span>
                      </>
                    )}
                  </button>
                  <p className="ti-secure-note">
                    🔒 100% secure · No spam calls
                  </p>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
